/**
 * ehr#727 (PEER_SUPPORT_NOTE_10-30-V8, Jay's 11a, 12a and 13a): a Peer support note's own parts.
 *
 * - Recovery goal: one line in the client's own words, above What happened (body.extras, a layout
 *   box: optional, never written by Generate). A new note starts with the last signed Peer support
 *   note's, marked "From MM/DD/YYYY" until it is edited.
 * - Peer support activities: pills above What happened, more than one picked (body.extras, a
 *   layout checklist), from the practice's list; "+ Add" puts a new one on that list.
 * - Peer specialist credential: shown under the signature, from the signer's own record; a note
 *   with none can't be signed (never skippable) and keeps the one it was signed with
 *   (body.peerCredential).
 */
import { z } from 'zod';
export const PEER_SUPPORT_KIND = 'Peer support';
/** The body.extras ids of the two parts the Peer support layout adds above What happened. */
export const RECOVERY_GOAL_ID = 'recoveryGoal';
export const PEER_ACTIVITIES_ID = 'peerActivities';
export const RECOVERY_GOAL_LABEL = 'Recovery goal';
export const PEER_ACTIVITIES_LABEL = 'Peer support activities';
export const PEER_CREDENTIAL_LABEL = 'Peer specialist credential';
/** V8 PEER_ACTS: the practice's list until someone adds to it. */
export const PEER_ACTIVITIES = [
    'Recovery planning',
    'Sharing lived experience',
    'Linking to resources',
    'Skill building',
    'Advocacy',
    'Going with the client to appointments',
];
/** V8 PEER_CREDS: the credentials the picker offers until someone adds one. */
export const PEER_CREDENTIALS = [
    'CPSS',
    'CPRS',
    'CPS',
    'CRPS',
    'PRSS',
    'Medi-Cal Peer Support Specialist',
];
/** An activity's length (V8 maxlength 60), a credential's (V8 maxlength 40), and list sizes. */
export const PEER_ACTIVITY_MAX = 60;
export const PEER_CREDENTIAL_MAX = 40;
export const PEER_LIST_MAX = 60;
const cleanName = (s) => s.replace(/\s+/g, ' ').trim();
/**
 * A list with one more name on it (V8 peerActAdd / peerCredSave): spaces tidied; a name already
 * on it (any case) is not added again and comes back as the list spells it. Activities start with
 * a capital letter, as V8 adds them; a credential keeps the letters as typed. `added` is false when
 * it was already there, or the list is full.
 */
export function withPeerName(list, raw, kind) {
    const t = cleanName(raw);
    const at = list.findIndex((x) => x.toLowerCase() === t.toLowerCase());
    if (!t || at >= 0)
        return { list: [...list], name: at >= 0 ? list[at] : '', added: false };
    if (list.length >= PEER_LIST_MAX)
        return { list: [...list], name: '', added: false };
    const name = kind === 'activities' ? t.charAt(0).toUpperCase() + t.slice(1) : t;
    return { list: [...list, name], name, added: true };
}
const peerNames = (max) => z
    .array(z.string().trim().min(1).max(max))
    .max(PEER_LIST_MAX)
    .refine((l) => new Set(l.map((x) => x.toLowerCase())).size === l.length, {
    message: 'Each name once.',
});
/** The practice's lists as stored in its clinical settings (not set: the V8 lists). */
export const peerActivitiesSchema = peerNames(PEER_ACTIVITY_MAX);
export const peerCredentialsSchema = peerNames(PEER_CREDENTIAL_MAX);
/** The practice's lists, the V8 ones for a list never saved (or one stored badly). */
export function readPeerLists(settings) {
    const o = settings && typeof settings === 'object' && !Array.isArray(settings)
        ? settings
        : {};
    const a = peerActivitiesSchema.safeParse(o.peerActivities);
    const c = peerCredentialsSchema.safeParse(o.peerCredentials);
    return {
        activities: a.success ? a.data : [...PEER_ACTIVITIES],
        credentials: c.success ? c.data : [...PEER_CREDENTIALS],
    };
}
/** POST /practice/peer-lists: one name for the activities or the credentials list. */
export const peerListAddSchema = z
    .object({
    list: z.enum(['activities', 'credentials']),
    name: z.string().trim().min(1, 'Type a name first.').max(PEER_ACTIVITY_MAX),
})
    .refine((v) => v.list === 'activities' || v.name.length <= PEER_CREDENTIAL_MAX, {
    message: `A credential is at most ${PEER_CREDENTIAL_MAX} characters.`,
    path: ['name'],
});
/** PUT /me/peer-credential: the signed-in person's own credential; '' takes it off. */
export const peerCredentialSchema = z.object({
    credential: z.string().trim().max(PEER_CREDENTIAL_MAX),
});
/** A body's Recovery goal (body.extras.recoveryGoal), '' when it has none. */
export function recoveryGoalOf(body) {
    const ex = body?.extras;
    const v = ex && typeof ex === 'object' ? ex[RECOVERY_GOAL_ID] : undefined;
    return typeof v === 'string' ? v.trim() : '';
}
//# sourceMappingURL=peerSupport.js.map